/**
 * TransCelerate Patient Library — Section 7.1.1.1 Liver Event Stopping Criteria.
 *
 * STUB CONTENT: structure and decision-node IDs are stable for assembly/Mermaid patching.
 * Replace narrative thresholds and wording with official TransCelerate Patient Library text
 * when available. Do not change node IDs (stopCriteria, monitorPath, restartBranch, permDisc,
 * notifySponsor, followUp) without updating patientLibrarySection7111.js.
 */

const STUB_BANNER =
  '[STUB — Replace with official TransCelerate Liver Event Stopping Criteria Patient Library text.]';

function buildNarrative({ phaseLabel, enzymeLabel, stopRuleSummary }) {
  return [
    '7.1.1.1 Liver event stopping criteria',
    '',
    STUB_BANNER,
    '',
    `Discontinuation of trial intervention for abnormal liver tests is required by the investigator when a participant meets one of the conditions outlined in the ${phaseLabel} ${enzymeLabel} liver event stopping algorithm below, or in the presence of abnormal liver chemistries not meeting protocol-specified stopping rules if the investigator believes that it is in the best interest of the participant.`,
    '',
    'Stopping criteria (standard TransCelerate algorithm — stub thresholds):',
    stopRuleSummary,
    '',
    'Follow-up actions:',
    '- Immediately interrupt trial intervention.',
    '- Repeat liver chemistry tests within 24–72 hours (or sooner if clinically indicated).',
    '- Perform directed liver evaluation per Appendix 12, Section 12.4 Liver Safety: Suggestions and Guidelines for Liver Events.',
    '- Notify the sponsor medical monitor within 24 hours of meeting stopping criteria.',
    '',
    'Footnotes:',
    'ᵃ ULN = upper limit of normal as defined by the local laboratory.',
    'ᵇ Local and central labs may be used per protocol-specified procedures.',
    '',
    'Refer to Appendix 12, Section 12.4 for detailed liver safety guidance and documentation requirements.',
  ].join('\n');
}

const MONITORING_STANDARD = [
  'Increased monitoring pathway (standard TransCelerate — stub):',
  '- Participants who do not meet liver stopping criteria but have elevated liver chemistries requiring continued observation will follow the standard increased monitoring pathway.',
  '- Repeat ALT/AST, alkaline phosphatase, and total bilirubin at a frequency specified in the algorithm (typically within 48–72 hours, then at intervals until stabilization or resolution).',
  '- Continue trial intervention only if stopping criteria are not met and the investigator considers continued dosing appropriate.',
  '- Document all findings in source records and the eCRF; escalate to sponsor if values worsen toward stopping thresholds.',
].join('\n');

const RESTART_DENIED = [
  '7.1.3.1 Trial intervention restart or rechallenge after liver stopping criteria are met',
  '',
  'Trial intervention restart or rechallenge after liver chemistry stopping criteria are met by any participant in this trial are not allowed.',
  '',
  'Refer to Appendix 12, Section 12.4 Liver Safety: Suggestions and Guidelines for Liver Events.',
].join('\n');

const RESTART_ALLOWED_SHELL = [
  '7.1.3.1 Trial intervention restart or rechallenge after liver stopping criteria are met',
  '',
  'Trial intervention restart/rechallenge after liver chemistry stopping criteria are met is allowed in this trial. If the participant meets liver chemistry stopping criteria, do not restart/rechallenge the participant with trial intervention unless:',
  '- Sponsor medical monitor (or designated board) approval is granted',
  '- Ethics and/or IRB approval is obtained, if required, and',
  '- Separate consent for intervention restart/rechallenge is signed by the participant',
  '',
  'NOTE: If trial intervention was interrupted for suspected intervention-induced liver injury, the participant should be informed of the risk of death, liver transplantation, hospitalization, and jaundice and reconsented before resumption of dosing.',
  '',
  'Refer to Appendix 12, Section 12.4 Liver Safety: Suggestions and Guidelines for Liver Events for details on the restart/rechallenge process.',
  '',
  'If approval to restart/rechallenge the participant with trial intervention is not granted, then the participant must permanently discontinue trial intervention and may continue in the trial for protocol-specified follow-up assessments.',
].join('\n');

/**
 * Mermaid template with stable node IDs for patching.
 * Restart branch is included; assembler removes it when restart is not permitted.
 */
function buildMermaid({ phaseLabel, enzymeLabel, stopDecisionLabel }) {
  return [
    'flowchart TD',
    `  startNode["Liver chemistry elevation detected<br/>${phaseLabel} / ${enzymeLabel}"]`,
    `  stopCriteria{"${stopDecisionLabel}"}`,
    '  monitorPath["Increased monitoring pathway<br/>(standard TransCelerate)"]',
    '  notifySponsor["Notify sponsor within 24h<br/>Directed liver evaluation<br/>Appendix 12.4"]',
    '  followUp["Follow-up liver tests and clinical assessment"]',
    '  restartBranch{"Restart/rechallenge permitted<br/>and approvals obtained?"}',
    '  permDisc["Permanent discontinuation of<br/>trial intervention"]',
    '  continueTx["Continue / resume monitoring<br/>per protocol"]',
    '',
    '  startNode --> stopCriteria',
    '  stopCriteria -->|Yes meets stopping criteria| notifySponsor',
    '  stopCriteria -->|No but monitoring required| monitorPath',
    '  monitorPath --> followUp',
    '  followUp --> stopCriteria',
    '  notifySponsor --> restartBranch',
    '  restartBranch -->|No| permDisc',
    '  restartBranch -->|Yes| continueTx',
  ].join('\n');
}

const STOP_ALT_ONLY_P1 =
  '- ALT ≥ [STUB threshold × ULN] with or without symptoms as defined for Phase 1\n- Or ALT meeting Hy\'s Law–equivalent / concomitant bilirubin rules per Phase 1 ALT-only algorithm';

const STOP_ALT_AST_P1 =
  '- ALT or AST ≥ [STUB threshold × ULN] with or without symptoms as defined for Phase 1\n- Or ALT or AST meeting Hy\'s Law–equivalent / concomitant bilirubin rules per Phase 1 ALT or AST algorithm';

const STOP_ALT_ONLY_P23 =
  '- ALT ≥ [STUB threshold × ULN] with or without symptoms as defined for Phase 2/3\n- Or ALT meeting Hy\'s Law–equivalent / concomitant bilirubin rules per Phase 2/3 ALT-only algorithm';

const STOP_ALT_AST_P23 =
  '- ALT or AST ≥ [STUB threshold × ULN] with or without symptoms as defined for Phase 2/3\n- Or ALT or AST meeting Hy\'s Law–equivalent / concomitant bilirubin rules per Phase 2/3 ALT or AST algorithm';

export const LIVER_LIBRARY_VARIANTS = {
  phase_1__alt_only: {
    key: 'phase_1__alt_only',
    phaseLabel: 'Phase 1',
    enzymeLabel: 'ALT Only',
    narrative: buildNarrative({
      phaseLabel: 'Phase 1',
      enzymeLabel: 'ALT Only',
      stopRuleSummary: STOP_ALT_ONLY_P1,
    }),
    mermaid: buildMermaid({
      phaseLabel: 'Phase 1',
      enzymeLabel: 'ALT Only',
      stopDecisionLabel: 'Meets Phase 1 ALT-only stopping criteria?',
    }),
    monitoringNarrative: MONITORING_STANDARD,
    restartDenied: RESTART_DENIED,
    restartAllowedShell: RESTART_ALLOWED_SHELL,
  },
  phase_1__alt_or_ast: {
    key: 'phase_1__alt_or_ast',
    phaseLabel: 'Phase 1',
    enzymeLabel: 'ALT or AST',
    narrative: buildNarrative({
      phaseLabel: 'Phase 1',
      enzymeLabel: 'ALT or AST',
      stopRuleSummary: STOP_ALT_AST_P1,
    }),
    mermaid: buildMermaid({
      phaseLabel: 'Phase 1',
      enzymeLabel: 'ALT or AST',
      stopDecisionLabel: 'Meets Phase 1 ALT or AST stopping criteria?',
    }),
    monitoringNarrative: MONITORING_STANDARD,
    restartDenied: RESTART_DENIED,
    restartAllowedShell: RESTART_ALLOWED_SHELL,
  },
  phase_2_3__alt_only: {
    key: 'phase_2_3__alt_only',
    phaseLabel: 'Phase 2/3',
    enzymeLabel: 'ALT Only',
    narrative: buildNarrative({
      phaseLabel: 'Phase 2/3',
      enzymeLabel: 'ALT Only',
      stopRuleSummary: STOP_ALT_ONLY_P23,
    }),
    mermaid: buildMermaid({
      phaseLabel: 'Phase 2/3',
      enzymeLabel: 'ALT Only',
      stopDecisionLabel: 'Meets Phase 2/3 ALT-only stopping criteria?',
    }),
    monitoringNarrative: MONITORING_STANDARD,
    restartDenied: RESTART_DENIED,
    restartAllowedShell: RESTART_ALLOWED_SHELL,
  },
  phase_2_3__alt_or_ast: {
    key: 'phase_2_3__alt_or_ast',
    phaseLabel: 'Phase 2/3',
    enzymeLabel: 'ALT or AST',
    narrative: buildNarrative({
      phaseLabel: 'Phase 2/3',
      enzymeLabel: 'ALT or AST',
      stopRuleSummary: STOP_ALT_AST_P23,
    }),
    mermaid: buildMermaid({
      phaseLabel: 'Phase 2/3',
      enzymeLabel: 'ALT or AST',
      stopDecisionLabel: 'Meets Phase 2/3 ALT or AST stopping criteria?',
    }),
    monitoringNarrative: MONITORING_STANDARD,
    restartDenied: RESTART_DENIED,
    restartAllowedShell: RESTART_ALLOWED_SHELL,
  },
};

export function liverVariantKey(phaseBucket, liverAlgorithm) {
  return `${phaseBucket}__${liverAlgorithm}`;
}
